import axios, { AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import { createAuthenticatedAxiosInstance } from './authenticatedAxios';

const axiosInstance: AxiosInstance = createAuthenticatedAxiosInstance();

export interface Result<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginatedList<T> {
  items: T[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  sort: string;
  direction: string;
}

export interface Order {
  id: number;
  statusId: number;
  status: string;
  emergencyOrder: boolean;
  submitted: boolean;
  orderTypeId: number;
  orderType: string;
  deliveryDate: string;
  cutOffDate: string;
  cutOffTime: string;
  totalCost: number;
  totalQuantity: number;
  supplierName:string;
}

export interface OrderDetails {
  id: number;
  statusId: number;
  status: string;
  emergencyOrder: boolean;
  submitted: boolean;
  orderTypeId: number;
  orderType: string;
  deliveryDate: string;
  cutOffDate: string;
  cutOffTime: string;
  supplierNumber: string;
  supplierName: string;
  cutOffTimeLeft: string;
  sapOrderNumber:string;
  deliveryNumber:string;
  items: OrderItemDetails[];
}

export interface OrderItemDetails {
  id: number;
  itemId: number;
  sku: string;
  name: string;
  primaryUom: string;
  orderableUom: string;
  price: number;
  categoryId: number;
  packSize: string
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  quantity: number;
  confirmedQuantity: number;
  shippedQuantity: number;
  imageId: number;
  thumbnailUrl: string;
  maxQuantity: number;
  minQuantity: number;
}

export interface OrderItem {
  id: number;
  sku: string;
  quantity: number;
}

export interface OrderType {
  id: number;
  name: string;
  cutOffTime: string;
}

export interface Category {
  id: number;
  name: string;
  display: boolean;
  showInFilters: boolean;
  showInNavigation: boolean;
  priority: number;
  fullCategoryString: string;
  parentId: number | null;
  parentName: string;
  parentFullCategoryString: string;
}

export interface Item {
  id: number;
  sku: string;
  name: string;
  categoryId: number;
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  price: number;
  packSize: string;
  primaryUom: string;
  orderableUom: string;
  barcode: string;
  barcodeUom: string;
  imageId: number;
  thumbnailUrl: string;
  maxQuantity: number;
  minQuantity: number;
  par1: number;
  par2: number;
  gtin?: string;
  isFrozen: boolean;
  shelfLife?: number | string;
  isNew?: boolean;
  newItemNumber?: string | undefined;
}
export interface ItemForOrder {
  id: number;
  sku: string;
  name: string;
  categoryId: number;
  category: string;
  stockOnHand: number;
  stockOnOrder: number;
  price: number;
  primaryUom: string;
  orderableUom: string;
  barcode: string;
  barcodeUom: string;
  imageId: number;
  thumbnailUrl: string;
}

export interface AddedItem {
  item: ItemForOrder;
  quantity: number;
  isNew: boolean;
  maxQuantity: number;
  minQuantity: number;
}

export interface DeliverySchedule {
  id: number;
  customerId: number;
  orderType: number;
  cutOffDate: string;
  cutOffTime: string;
  shipDate: string;
  deliveryDate: string;
  supplier: string;
  distributionCenter: string;
}

export interface OrdersFilterOptions {
  storeNumber: string | undefined;
  orderNumber: string | undefined;
  item: string | undefined;
  orderType: number;
  status: number;
  deliveryDateFrom: string | undefined;
  deliveryDateTo: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}

export interface ItemsFilterOptions {
  storeNumber: string | undefined;
  item: string | undefined;
  categoryId: number;
  orderType: number;
  supplier: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
}
export interface ItemsParFilterOptions {
  storeNumber: string | undefined;
  item: string | undefined;
  categoryId: number;
  orderType: number;
  supplier: string | undefined;
  pageNumber: number;
  pageSize: number;
  sort: string | undefined;
  direction: string | undefined;
  hasParLevel:boolean | undefined;
}

export interface ItemFilterParams {
  marketName: string | undefined;
  storeNumber: string | undefined;
  search: string | undefined;
  pageNumber: number;
  pageSize: number;
}

export interface SimpleCategory {
  id: number;
  name: string;
}

export interface CreateOrderRequest {
  storeNumber: string;
  emergencyOrder: boolean;
  orderTypeId: number;
  deliveryDate: string;
  cutOffDate: string;
  supplierNumber: string;
  items: OrderItem[];
}

export interface OrderItem {
  id: number;
  sku: string;
  quantity: number;
}

export interface DeleteOrderItemsRequest {
  orderId: number;
  items: number[];
}

export interface AddOrderItemRequest {
  orderId: number;
  itemId: number;
  quantity: number;
  predictedQuantity?: number;
}

export interface UpdateItemParRequest {
  itemId: number;
  par1: number;
  par2: number;
}

export interface OrderItemUpdate {
  id: number;
  quantity: number;
}

export interface OrderUpdate {
  id: number;
  items: OrderItemUpdate[];
}

export interface OrderForm {
  categories: OrderFormCategory[];
  availableDates: DeliveryDates[];
  isOrderTypeAutoship: boolean;
  totalPages: number;
  forecastCutOffTime: string;
  lastSaved: string;
}
export interface DeliveryDates {
  deliveryDate: string;
  cutOffTime: string;
  cutOffDate: string; 
  hasOds?: boolean | null;
}

export interface OrderFormCategory extends Category {
  items: OrderFormItem[];
}

export interface OrderFormItem extends Item {
  quantity: number[];
  lastTimeSold: string;
}

export interface OrderFormUpdate {
  storeNumber: string;
  orderTypeId: number;
  supplierNumber: string;
  parValue?: number | undefined;
  items: OrderFormItemUpdate[];
  dates: string[];
}

export interface OrderFormItemUpdate {
  id: number;
  sku: string;
  dates: OrderFormItemUpdateDate[];
}

export interface OrderFormItemUpdateDate {
  date: string;
  quantity: number;
  predictedQuantity?: number;
}

export interface FileResponse {
  fileData: string;
  fileName: string;
}

export interface FileRequest {
  fileData: string;
  fileName: string;
}

export interface Supplier {
  id: number;
  name: string;
  number: string;
}

export interface DsdSupplier extends Supplier {
  enabled: boolean;
}

export interface News{
  id: number;
  title: string;
  teaser: string;
  body: string;
  imageId: number;
  thumbnailUrl: string;
  dataFromOM: boolean;
  startDate:string;
  imagesUrl: string[] | null | undefined;
  files: Files[] | null;
}

export interface Files {
  fileUrl: Item;
  fileDescription: number; 
}
export interface OutOfStockUrgentAction { 
  context: string; 
}

export interface DefaultOrderSettings { 
  isStarbucksStore: boolean;
  isFreshAndDairyEnabled: boolean;
  isAmbientEnabled: boolean;
  isWarningEmailEnabled: boolean;
  dsdSuppliers: DsdSupplier[];
}

export interface OrderFormFilterOptions {
  storeNumber: string | undefined;
  orderTypeId: number;
  categoryId: number;
  orderBy: string;
  direction: string;
  item: string;
  supplierNumber: string;
  pageNumber: number;
  pageSize: number;
} 
export interface TrainingAreaMaterials {  
  Id: number;
  companyId: number;
  FileName: string;
  blobName: string;
  blobUrl: string;
  uploadedDate: string;
  uploadedBy: number;
  documentType: string; 
}

export interface ForecastOrdersReportRequest {
  storeNumbers: string[],
  fromDate: string,
  toDate: string
}

export interface TimeTrackingWidgetDataRequest {
  storeNumber: string,
  azureApplicationId: string
}

export interface AIWidgetData {
  Value: string,
  ProgressPercentage: number | undefined,
  ChangeFromPreviousPeriodPercentage: number | undefined
}

export interface OrderFormFilterOptions {
  storeNumber: string | undefined;
  orderTypeId: number;
  categoryId: number;
  orderBy: string;
  direction: string;
  item: string;
  supplierNumber: string;
  pageNumber: number;
  pageSize: number;
}

export interface AddOrUpdateBarcodeMappingRequest {
    Id?: number;
    ItemNumber: string;
    Barcode: string;
    Uom: string;
}

export interface BarcodeMappingDto {
    Id: number;
    Name: string;
    ItemNumber?: string;
    SKU: string;
    UOM: string;
    Barcode: string;
    StoreNumber: string;
}

export interface BarcodeRowResult {
  RowNumber: number;
  IsSuccess: boolean;
  Message: string;
}

export interface OrderFormFilterOptions {
  storeNumber: string | undefined;
  orderTypeId: number;
  categoryId: number;
  orderBy: string;
  direction: string;
  item: string;
  supplierNumber: string;
  pageNumber: number;
  pageSize: number;
}

class Client {

  private static instance: Client;
  private axiosInstance: AxiosInstance;

  private constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  public static getInstance(): Client {
    if (!Client.instance) {
      Client.instance = new Client(axiosInstance);
    }
    return Client.instance;
  }

  public async getOrders(options: OrdersFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Order>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        OrderNumber: options.orderNumber ?? '',
        Item: options.item ?? '',
        OrderType: options.orderType.toString() ?? '',
        Status: options.status.toString() ?? '',
        DeliveryDateFrom: options.deliveryDateFrom ?? '',
        DeliveryDateTo: options.deliveryDateTo ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Order>>> = await this.axiosInstance.get(`orders?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderAudit(storeNumber: string | undefined, orderNumber: number, cancelToken?: CancelToken): Promise<Result<(string | undefined | null)[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<(string | undefined | null)[]>> = await this.axiosInstance.get(`orders/audit/${orderNumber}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderTypes(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OrderType[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderType[]>> = await this.axiosInstance.get(`ordertypes?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDeliverySchedules(storeNumber: string | undefined, orderType: number | undefined, supplierNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DeliverySchedule[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
        OrderType: orderType?.toString() ?? '',
        SupplierNumber: supplierNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DeliverySchedule[]>> = await this.axiosInstance.get(`deliveryschedules?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItems(options: ItemsFilterOptions, isPar = false, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        Item: options.item ?? '',
        OrderType: options.orderType.toString() ?? '',
        Category: options.categoryId?.toString() ?? '',
        Supplier: options.supplier?.toString() ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        Par: isPar.toString(),
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemsPar(options: ItemsParFilterOptions, isPar = false, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: options.storeNumber ?? '',
        Item: options.item ?? '',
        OrderType: options.orderType.toString() ?? '',
        Category: options.categoryId?.toString() ?? '',
        Supplier: options.supplier?.toString() ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
        Par: isPar.toString(),
        HasParLevel: options.hasParLevel !== undefined ? options.hasParLevel.toString() : 'false',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemsByFranchiseName(options: ItemFilterParams, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    try {
      const params = new URLSearchParams({
        MarketName: options.marketName ?? '',
        StoreNumber: options.storeNumber ?? '',
        Search: options.search ?? '',
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products/getbymarketname?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCategories(storeNumber: string | undefined, orderType: number, supplier: string | undefined, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
        OrderType: orderType.toString(),
        Supplier: supplier?.toString() ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`categories?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async createOrder(request: CreateOrderRequest, cancelToken?: CancelToken): Promise<Result<number>> {
    try {
      const headers = { 'ews-storeNumber': request.storeNumber };
      const response: AxiosResponse<Result<number>> = await this.axiosInstance.post<Result<number>>('orders', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrder(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteOrderItem(storeNumber: string | undefined, orderId: string, orderItemId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const url = `orders/${orderId}/OrderItems/${orderItemId}`;
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(url, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async addOrderItem(storeNumber: string | undefined, request: AddOrderItemRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/orderitems/add', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async updateItemPar(storeNumber: string | undefined, request: UpdateItemParRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('items/updateitempar', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteOrderItems(storeNumber: string | undefined, request: DeleteOrderItemsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/deleteorderitems', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async submitOrder(storeNumber: string | undefined, orderId: string, orderDetails: OrderDetails, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/submit/${orderId}`, orderDetails, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getItemByBarcode(storeNumber: string | undefined, barcode: string, stockCount: string | undefined, user: string | undefined, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber, 'stock-count': stockCount, 'user': user };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/barcode/${barcode}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItemBySku(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/sku/${sku}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateOrder(storeNumber: string | undefined, request: OrderUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/update', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getOrderRejectionReason(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<string>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<string>> = await this.axiosInstance.get(`orders/RejectionReason/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async isOrderAdminEdited(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.get(`orders/IsOrderAdminEdited/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getCommentForOrder(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<string>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<string>> = await this.axiosInstance.get(`orders/GetCommentForOrder/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getOrderByDeliveryDate(storeNumber: string | undefined, orderTypeId: number, deliveryDate: string, supplierNumber: string, emergencyOrder: boolean, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    try {
      const params = new URLSearchParams({
        SupplierNumber: supplierNumber,
        EmergencyOrder: emergencyOrder.toString(),
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/ordertype/${orderTypeId.toString()}/deliverydate/${deliveryDate}?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getItem(storeNumber: string | undefined, itemId: number, cancelToken?: CancelToken): Promise<Result<Item>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/${itemId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async copyOrder(storeNumber: string | undefined, orderId: string | undefined, copyOrderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/${orderId}/copy/${copyOrderId}`, null, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteOrder(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`orders/${orderId}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async getOrderForm(options: OrderFormFilterOptions, cancelToken?: CancelToken): Promise<Result<OrderForm>> {
    try {
      const params = new URLSearchParams({
        OrderType: options.orderTypeId.toString() ?? '',
        Category: options.categoryId.toString() ?? '',
        Sort: options.orderBy ?? '',
        Direction: options.direction ?? '',
        Item: options.item ?? '',
        SupplierNumber: options.supplierNumber ?? '',
        PageNumber: options.pageNumber.toString() ?? '',
        PageSize: options.pageSize.toString() ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<OrderForm>> = await this.axiosInstance.get(`orders/orderform?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getForecastOrderForm(options: OrderFormFilterOptions, cancelToken?: CancelToken): Promise<Result<OrderForm>> {
    try {
      const params = new URLSearchParams({
        OrderType: options.orderTypeId.toString() ?? '',
        Category: options.categoryId.toString() ?? '',
        Sort: options.orderBy ?? '',
        Direction: options.direction ?? '',
        Item: options.item ?? '',
        SupplierNumber: options.supplierNumber ?? '',
        PageNumber: options.pageNumber.toString() ?? '',
        PageSize: options.pageSize.toString() ?? '',
      }).toString();

      const headers = { 'ews-storeNumber': options.storeNumber };
      const response: AxiosResponse<Result<OrderForm>> = await this.axiosInstance.get(`orders/forecastorderform?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async saveOrderForm(storeNumber: string | undefined, request: OrderFormUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/orderform', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async saveForecastOrderForm(storeNumber: string | undefined, request: OrderFormUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/forecastorderform', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async downloadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, isForecastOrderForm?: boolean, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({ OrderType: orderTypeId.toString(), Supplier: supplierNumber, IsForecastOrderForm: isForecastOrderForm?.toString() ?? 'false' }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`orders/orderform/download?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadParConfigurationForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, category: number, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        Item: '',
        OrderType: orderTypeId.toString() ?? '',
        Category: category.toString() ?? '',
        Supplier: supplierNumber?.toString() ?? '',
        Sort: 'Name',
        Direction: 'asc',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`paritems/parconfigurationform/download?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({ Supplier: supplierNumber }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/orderform/upload/${orderTypeId}?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadForecastOrderForm(storeNumber: string | undefined, orderTypeId: number, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/forecastorderform/upload/${orderTypeId}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async loadLastWeekForecast(storeNumber: string | undefined, orderTypeId: number, itemIdList: number[], cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/forecastorderform/loadlastweekforecast/${orderTypeId}`, itemIdList, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async uploadParConfigurationForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({ Supplier: supplierNumber }).toString();
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`items/parconfigurationform/upload/${orderTypeId}?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getSuppliers(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<Supplier[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<Supplier[]>> = await this.axiosInstance.get('suppliers', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async GetNews(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<News[]>> {
    try {
      const params = new URLSearchParams({
        StoreNumber: storeNumber ?? '',
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<News[]>> = await this.axiosInstance.get(`news?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getTimeTrackingWidgetData(storeNumber: string, applicationId: string, cancelToken?: CancelToken): Promise<Result<AIWidgetData>> {
    try {
      const payload: TimeTrackingWidgetDataRequest = {
        storeNumber: storeNumber,
        azureApplicationId: applicationId,
      };
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.post<Result<AIWidgetData>>('orders/timespentordering', payload, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  public async GetOutOfStockAlert(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('outOfStockAlert', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async GetUrgentAlerts(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('urgentAlert', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getDefaultOrderSettings(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DefaultOrderSettings>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<DefaultOrderSettings>> = await this.axiosInstance.get('customers/getdefaultordersettings', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateDefaultOrderSettings(storeNumber: string | undefined, request: DefaultOrderSettings, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('customers/updatedefaultordersettings', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async generateForecastOrdersReport(storeNumber: string, request: ForecastOrdersReportRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.post<Result<FileResponse>>('orders/generateforecastordersreport', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async addOrUpdateBarcodeMapping(
    storeNumber: string | undefined,
    request: AddOrUpdateBarcodeMappingRequest,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response = await this.axiosInstance.post<Result<boolean>>(
        'barcodeMappings',
        request,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async deleteBarcodeMapping(
    storeNumber: string | undefined,
    id: number,
    cancelToken?: CancelToken,
  ): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response = await this.axiosInstance.delete<Result<boolean>>(
        `barcodeMappings/${id}`,
        { headers, cancelToken },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: false };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: false };
      }
    }
  }

  public async bulkImportBarcodeMapping(
    storeNumber: string | undefined,
    file: FileRequest,
    cancelToken?: CancelToken,
  ): Promise<Result<BarcodeRowResult[] | undefined>> {
    try {
      const headers = {
        'ews-storeNumber': storeNumber ?? '',
      };
      const response: AxiosResponse<Result<BarcodeRowResult[] | undefined>> = await this.axiosInstance.post('barcodeMappings/bulkAdd', file, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: undefined };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: undefined };
      }
    }
  }

  public async getBulkImportBarcodeMappingTemplate(
    storeNumber: string | undefined,
    cancelToken?: CancelToken,
  ): Promise<Result<FileResponse>> {
    try {
      const headers = {
        'ews-storeNumber': storeNumber ?? '',
      };
      const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get('barcodeMappings/bulkAdd/template', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
        return { success: false, message: error.message, data: { fileData: '', fileName: '' } };
      } else {
        console.error('Unexpected error:', error);
        return { success: false, message: 'An unexpected error occurred.', data: { fileData: '', fileName: '' } };
      }
    }
  }

  public async getBarcodeMappings(
    storeNumber: string | undefined,
    barcode: string,
    itemSKU: string,
    itemNumberFilter: string,
    page: number,
    pageSize: number,
    sort: string,
    sortDirection: string,
    cancelToken?: CancelToken,
  ): Promise<Result<PaginatedList<BarcodeMappingDto>>> {
    try {
      const params = new URLSearchParams({
        barcodeFilter: barcode,
        skuFilter: itemSKU,
        itemNumberFilter: itemNumberFilter,
        pageNumber: page.toString(),
        pageSize: pageSize.toString(),
        sort: sort,
        direction: sortDirection,
      }).toString();
      const headers = { 'ews-storeNumber': storeNumber };
      const response = await this.axiosInstance.get<Result<PaginatedList<BarcodeMappingDto>>>(`barcodeMappings?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async getTrainingMaterials(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<TrainingAreaMaterials[]>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<TrainingAreaMaterials[]>> = await this.axiosInstance.get('gettrainingmaterials', { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteTrainingMaterial(storeNumber: string | undefined, materialId: string, fileName: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`deletetrainingmaterial/${materialId}`, { headers, cancelToken, params: { fileName } });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  public async uploadTrainingArea(storeNumber: string, request: FileRequest[], cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('trainingareaupload', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async downloadTrainingArea(storeNumber: string | undefined, filename: string, cancelToken?: CancelToken): Promise<Blob> {
    try {
      const headers = { 'ews-storeNumber': storeNumber };
      const response = await this.axiosInstance.get('downloadtrainingarea', {
        headers,
        cancelToken,
        responseType: 'blob',
        params: { fileName: filename },
      });
      return response.data as Blob;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async bulkDeleteMaterials(storeNumber: string | undefined, fileNames: string[], cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      // Make a POST request to the bulk delete endpoint with the list of fileNames
      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('deletetrainingmaterialsbulk', fileNames, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

}

export const client = Client.getInstance();
